import React, { FormEvent, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import {
  NOTISTACK_FAILURE_OPTIONS,
  NOTISTACK_SUCCESS_OPTIONS,
} from "constants/index";
import {
  FEATURE_FLAG_NAME_PATTERN,
  FeatureFlag,
  useCreateFeatureFlagMutation,
  useDeleteFeatureFlagMutation,
  useListFeatureFlagsQuery,
  useToggleFeatureFlagMutation,
} from "features/settings/featureFlags";

const NAME_RULE =
  "Lowercase letters, digits, underscores and hyphens; up to 100 characters.";

const MONOSPACE =
  'ui-monospace, "SFMono-Regular", "Menlo", "Consolas", monospace';

// Muted rather than removed: an old flag is still live, just no longer news.
const HIDDEN_ROW_SX = {
  bgcolor: "action.hover",
  "& td": { color: "text.secondary" },
};

const getNameValidationError = (name: string) => {
  if (name === "") {
    return "Name is required.";
  }
  if (!FEATURE_FLAG_NAME_PATTERN.test(name)) {
    return NAME_RULE;
  }
  return null;
};

interface FormErrors {
  name?: string;
  description?: string;
  form?: string;
}

const firstMessage = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "string") {
    return value[0];
  }
  return undefined;
};

/**
 * DRF answers a rejected create with `{field: [messages]}`, or with `detail`
 * for a refusal that is not about one field (a 403, say). Anything else —
 * a network failure, a 500 page — gets a generic line rather than nothing.
 */
const toFormErrors = (error: unknown): FormErrors => {
  const data =
    typeof error === "object" && error !== null && "data" in error
      ? (error as { data: unknown }).data
      : undefined;
  if (typeof data !== "object" || data === null) {
    return { form: "Failed to create the feature flag." };
  }
  const fields = data as Record<string, unknown>;
  const errors: FormErrors = {
    name: firstMessage(fields.name),
    description: firstMessage(fields.description),
    form: firstMessage(fields.non_field_errors) ?? firstMessage(fields.detail),
  };
  if (!errors.name && !errors.description && !errors.form) {
    errors.form = "Failed to create the feature flag.";
  }
  return errors;
};

const formatCreated = (createdOn: string) => {
  const created = dayjs(createdOn);
  return created.isValid() ? created.format("MMM D, YYYY") : "--";
};

interface CreateFeatureFlagDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateFeatureFlagDialog = ({
  open,
  onClose,
}: CreateFeatureFlagDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [serverErrors, setServerErrors] = useState<FormErrors>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createFeatureFlag, { isLoading: isCreating }] =
    useCreateFeatureFlagMutation();

  const normalizedName = name.trim();
  const clientNameError = nameTouched
    ? getNameValidationError(normalizedName)
    : null;
  const nameError = clientNameError ?? serverErrors.name;

  const resetAndClose = () => {
    setName("");
    setDescription("");
    setNameTouched(false);
    setServerErrors({});
    onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setNameTouched(true);
    if (getNameValidationError(normalizedName)) {
      return;
    }
    setServerErrors({});
    try {
      await createFeatureFlag({
        name: normalizedName,
        description: description.trim(),
      }).unwrap();
      enqueueSnackbar(
        `Feature flag "${normalizedName}" created.`,
        NOTISTACK_SUCCESS_OPTIONS,
      );
      resetAndClose();
    } catch (error) {
      setServerErrors(toFormErrors(error));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isCreating ? undefined : resetAndClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="create-feature-flag-title"
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id="create-feature-flag-title">
          New feature flag
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" mb={2}>
            A flag starts on outside production and off in production. Its name
            cannot be changed later.
          </DialogContentText>
          {serverErrors.form && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverErrors.form}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              autoFocus
              required
              size="small"
              label="Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setServerErrors((prev) => ({ ...prev, name: undefined }));
              }}
              onBlur={() => setNameTouched(true)}
              error={Boolean(nameError)}
              helperText={nameError ?? NAME_RULE}
              inputProps={{
                maxLength: 100,
                spellCheck: false,
                autoCapitalize: "off",
                autoComplete: "off",
                style: { fontFamily: MONOSPACE },
              }}
            />
            <TextField
              size="small"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={Boolean(serverErrors.description)}
              helperText={serverErrors.description ?? "Optional."}
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetAndClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isCreating}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface DeleteFeatureFlagDialogProps {
  flag: FeatureFlag | null;
  onClose: () => void;
}

const DeleteFeatureFlagDialog = ({
  flag,
  onClose,
}: DeleteFeatureFlagDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteFeatureFlag, { isLoading: isDeleting }] =
    useDeleteFeatureFlagMutation();

  const handleConfirm = async () => {
    if (!flag) {
      return;
    }
    try {
      await deleteFeatureFlag(flag.id).unwrap();
      enqueueSnackbar(
        `Feature flag "${flag.name}" deleted.`,
        NOTISTACK_SUCCESS_OPTIONS,
      );
      onClose();
    } catch {
      enqueueSnackbar(
        `Failed to delete feature flag "${flag.name}".`,
        NOTISTACK_FAILURE_OPTIONS,
      );
    }
  };

  return (
    <Dialog
      open={flag !== null}
      onClose={isDeleting ? undefined : onClose}
      aria-labelledby="delete-feature-flag-title"
    >
      <DialogTitle id="delete-feature-flag-title">
        Delete feature flag?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Box component="span" fontFamily={MONOSPACE} fontWeight={600}>
            {flag?.name}
          </Box>{" "}
          will be removed for every user. Code that still checks it will read it
          as off.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FeatureFlags = () => {
  const [includeHidden, setIncludeHidden] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState<FeatureFlag | null>(null);
  const [savingIds, setSavingIds] = useState<number[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError } = useListFeatureFlagsQuery({
    includeHidden,
  });
  const [toggleFeatureFlag] = useToggleFeatureFlagMutation();

  const flags = data ?? [];

  const handleToggle = async (flag: FeatureFlag, enabled: boolean) => {
    setSavingIds((prev) => [...prev, flag.id]);
    try {
      await toggleFeatureFlag({ id: flag.id, enabled }).unwrap();
    } catch {
      enqueueSnackbar(
        `Failed to turn ${enabled ? "on" : "off"} feature flag "${flag.name}".`,
        NOTISTACK_FAILURE_OPTIONS,
      );
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== flag.id));
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress aria-label="Loading feature flags" />
        </Box>
      );
    }
    if (isError) {
      return <Alert severity="error">Failed to load feature flags.</Alert>;
    }
    if (flags.length === 0) {
      return (
        <Box py={6} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            {includeHidden
              ? "No feature flags yet."
              : "No feature flags created in the last 6 months."}
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer>
        <Table size="small" aria-label="Feature flags">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Created</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="right" aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {flags.map((flag) => {
              const isSaving = savingIds.includes(flag.id);
              return (
                <TableRow
                  key={flag.id}
                  hover
                  sx={flag.is_hidden ? HIDDEN_ROW_SX : undefined}
                >
                  <TableCell sx={{ verticalAlign: "middle" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        component="code"
                        fontFamily={MONOSPACE}
                        fontSize={13}
                        fontWeight={600}
                        sx={{ wordBreak: "break-all" }}
                      >
                        {flag.name}
                      </Box>
                      {flag.is_hidden && (
                        <Tooltip title="Created more than 6 months ago">
                          <Chip
                            label="Hidden"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    {flag.description || (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.disabled"
                      >
                        No description
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <time dateTime={flag.created_on}>
                      {formatCreated(flag.created_on)}
                    </time>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={flag.enabled}
                      disabled={isSaving}
                      onChange={(_event, checked) =>
                        handleToggle(flag, checked)
                      }
                      inputProps={{ "aria-label": `Enable ${flag.name}` }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        aria-label={`Delete ${flag.name}`}
                        onClick={() => setFlagToDelete(flag)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Feature Flags
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Turn features on or off for every user. Flags created more than 6 months
        ago are hidden unless you ask for them.
      </Typography>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        mb={1}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={includeHidden}
              onChange={(e) => setIncludeHidden(e.target.checked)}
            />
          }
          label="Show hidden flags (created more than 6 months ago)"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New feature flag
        </Button>
      </Box>
      {renderBody()}
      <CreateFeatureFlagDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <DeleteFeatureFlagDialog
        flag={flagToDelete}
        onClose={() => setFlagToDelete(null)}
      />
    </Box>
  );
};

export default FeatureFlags;
